/**
 * US phone number validation
 *
 * This matches the backend PhoneValidator Ruby class validation rules:
 * - Accepts optional +1 or 1 prefix (or none)
 * - Allows common separators: space, -, ., , parentheses
 * - Enforces area code first digit is 2-9 (not 0 or 1, matching NANP)
 * - Enforces exchange code first digit is 2-9 (not 0 or 1, matching NANP)
 * - Requires exactly 10 digits after country code
 */

const US_PHONE_REGEX =
  /^(?:\+?1[\s.,-]?)?(?:\(([2-9]\d{2})\)|([2-9]\d{2}))[\s.,-]?([2-9]\d{2})[\s.,-]?\d{4}$/;

/**
 * Validates if a phone number matches the US phone number format
 *
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example Valid formats:
 * validatePhoneNumber("223 456 7890")       // true
 * validatePhoneNumber("223-456-7890")       // true
 * validatePhoneNumber("(223) 456-7890")     // true
 * validatePhoneNumber("+1 223 456 7890")    // true
 * validatePhoneNumber("1.223.456.7890")     // true
 *
 * @example Invalid formats:
 * validatePhoneNumber("123 456 7890")       // false (area code starts with 1)
 * validatePhoneNumber("+1 (123) 456 7890")  // false (area code starts with 1)
 * validatePhoneNumber("023 456 7890")       // false (starts with 0)
 */
export const validatePhoneNumber = phone => {
  if (!phone || typeof phone !== "string") {
    return false;
  }

  return US_PHONE_REGEX.test(phone.trim());
};

/**
 * Validates phone number and returns validation result with error message
 *
 * @param {string} phone - Phone number to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.required - Whether the phone number is required (default: true)
 * @param {string} options.emptyMessage - Custom message for empty phone (default: "Please enter a phone number")
 * @param {string} options.invalidMessage - Custom message for invalid phone (default: "Please enter a valid 10-digit phone number")
 * @returns {Object} { valid: boolean, message: string }
 *
 * @example
 * validatePhoneWithMessage("223-456-7890")
 * // Returns: { valid: true, message: "" }
 *
 * validatePhoneWithMessage("")
 * // Returns: { valid: false, message: "Please enter a phone number" }
 *
 * validatePhoneWithMessage("123-456-7890")
 * // Returns: { valid: false, message: "Please enter a valid 10-digit phone number" }
 */
export const validatePhoneWithMessage = (phone, options = {}) => {
  const {
    required = true,
    emptyMessage = "Please enter a phone number",
    invalidMessage = "Please enter a valid 10-digit phone number"
  } = options;

  if (!phone || typeof phone !== "string" || phone.trim() === "") {
    if (required) {
      return { valid: false, message: emptyMessage };
    }
    return { valid: true, message: "" };
  }

  if (!validatePhoneNumber(phone)) {
    return { valid: false, message: invalidMessage };
  }

  return { valid: true, message: "" };
};

/**
 * Gets the regex pattern used for phone validation
 * Useful for HTML5 pattern attribute or other validation libraries
 *
 * @returns {RegExp} The US phone number regex pattern
 */
export const getPhoneValidationRegex = () => US_PHONE_REGEX;

/**
 * Gets the regex pattern as a string (without delimiters)
 * Useful for HTML5 pattern attribute
 *
 * @returns {string} The US phone number regex pattern as string
 */
export const getPhoneValidationPattern = () => US_PHONE_REGEX.source;
